// Read-only viewer for an aura entry from History.
// Standalone aura entries (kind='aura') store the photo in `user-photos` and
// have no generated_image_url. This page just renders the cached aura over
// the original photo — no scanning, no extra spend.
import { useEffect, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { ArrowLeft, Sparkles } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { useSignedImage } from "@/hooks/useSignedImage";
import { AuraScoreRing } from "@/components/AuraScoreRing";
import { SEO } from "@/components/SEO";
import { MobileShell } from "@/components/mobile";
import { useIsMobile } from "@/hooks/use-mobile";

interface AuraRow {
  id: string;
  user_photo_url: string;
  generated_image_url: string | null;
  kind: string;
  aura_score: number | null;
  aura_silhouette: number | null;
  aura_proportions: number | null;
  aura_color_season: number | null;
  aura_statement: number | null;
  aura_photo_readiness: number | null;
  aura_verdict: string | null;
  aura_tips: string[] | null;
}

const SUB_LABELS: Array<[keyof AuraRow, string]> = [
  ["aura_silhouette", "Silhouette"],
  ["aura_proportions", "Proportions"],
  ["aura_color_season", "Color season"],
  ["aura_statement", "Statement factor"],
  ["aura_photo_readiness", "Photo-readiness"],
];

const AuraView = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { toast } = useToast();
  const isMobile = useIsMobile();
  const [row, setRow] = useState<AuraRow | null>(null);
  const [loading, setLoading] = useState(true);

  // SEO handled by <SEO /> component in render.

  useEffect(() => {
    if (!id) return;
    (async () => {
      const { data, error } = await supabase
        .from("tryons")
        .select(
          "id, user_photo_url, generated_image_url, kind, aura_score, aura_silhouette, aura_proportions, aura_color_season, aura_statement, aura_photo_readiness, aura_verdict, aura_tips",
        )
        .eq("id", id)
        .maybeSingle();
      if (error) {
        toast({ title: "Couldn't load aura", description: error.message, variant: "destructive" });
        setLoading(false);
        return;
      }
      setRow((data ?? null) as AuraRow | null);
      setLoading(false);
    })();
  }, [id, toast]);

  // Aura entries store the photo in user-photos; tryon entries store the
  // generated image in tryon-results. Pick whichever exists.
  const userPhoto = useSignedImage("user-photos", row?.user_photo_url);
  const generated = useSignedImage("tryon-results", row?.generated_image_url);
  const photoUrl = generated ?? userPhoto;

  if (loading) {
    return (
      <div className="mx-auto w-full max-w-md px-5 pt-10">
        <div className="aspect-[3/4] animate-pulse rounded-3xl bg-muted" />
      </div>
    );
  }

  if (!row || typeof row.aura_score !== "number") {
    return (
      <div className="mx-auto w-full max-w-md px-5 pt-10 text-center">
        <p className="text-sm text-muted-foreground">This aura isn't available.</p>
        <Button asChild variant="outline" className="mt-4">
          <Link to="/app/history">Back to history</Link>
        </Button>
      </div>
    );
  }

  const body = (
    <div className="mx-auto w-full max-w-md px-5 pt-6 pb-12 animate-fade-in">
      <div className="mb-4 flex items-center justify-between">
        <Button variant="ghost" size="icon" onClick={() => navigate(-1)} aria-label="Back">
          <ArrowLeft className="h-5 w-5" />
        </Button>
        <span className="font-display text-[10px] font-semibold uppercase tracking-[0.3em] text-primary">
          — AURA SCORE
        </span>
        <span className="w-9" />
      </div>

      <div className="text-center">
        <h1 className="font-display text-2xl font-bold tracking-tight">Aura Score</h1>
        <p className="mt-1 text-sm text-muted-foreground">Your editorial aura read</p>
      </div>

      {/* Photo */}
      <div className="relative mt-5 overflow-hidden rounded-3xl border border-primary/20 bg-card shadow-elevated">
        <div className="relative aspect-[3/4] w-full">
          {photoUrl ? (
            <img src={photoUrl} alt="Your aura photo" className="h-full w-full object-cover" />
          ) : (
            <div className="h-full w-full animate-pulse bg-muted" />
          )}
          <div className="absolute right-3 top-3">
            <div className="rounded-full border border-primary/40 bg-background/85 px-3 py-1 text-xs font-bold tabular-nums text-foreground backdrop-blur-md">
              {row.aura_score}/100
            </div>
          </div>
        </div>
      </div>

      {/* Result */}
      <div className="mt-6 rounded-3xl border border-primary/30 bg-gradient-to-br from-primary/5 to-transparent p-5 shadow-soft">
        <div className="flex flex-col items-center text-center">
          <AuraScoreRing score={row.aura_score} size={140} />
          {row.aura_verdict && (
            <p className="mt-4 max-w-xs text-sm leading-relaxed text-foreground/90">
              "{row.aura_verdict}"
            </p>
          )}
        </div>

        <div className="mt-6 space-y-2.5">
          {SUB_LABELS.map(([key, label]) => {
            const v = row[key] as number | null;
            if (typeof v !== "number") return null;
            return (
              <div key={key}>
                <div className="flex items-center justify-between text-xs">
                  <span className="text-muted-foreground">{label}</span>
                  <span className="font-semibold tabular-nums">{v}</span>
                </div>
                <div className="mt-1 h-1.5 w-full overflow-hidden rounded-full bg-muted">
                  <div
                    className="h-full rounded-full bg-gradient-to-r from-primary to-primary/60"
                    style={{ width: `${Math.max(0, Math.min(100, v))}%` }}
                  />
                </div>
              </div>
            );
          })}
        </div>

        {Array.isArray(row.aura_tips) && row.aura_tips.length > 0 && (
          <div className="mt-6">
            <p className="mb-2 flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wider text-primary">
              <Sparkles className="h-3.5 w-3.5" />
              Aura Tips
            </p>
            <ul className="space-y-2">
              {row.aura_tips.map((t, i) => (
                <li key={i} className="flex gap-2 text-sm">
                  <span className="mt-1 h-1.5 w-1.5 shrink-0 rounded-full bg-primary" />
                  <span>{t}</span>
                </li>
              ))}
            </ul>
          </div>
        )}

        <div className="mt-6">
          <Button asChild variant="outline" className="h-11 w-full">
            <Link to="/app/aura">Read another aura</Link>
          </Button>
        </div>
      </div>
    </div>
  );

  return (
    <>
      <SEO title="Aura · FitAura AI" description="View your AI style and aura analysis." noindex />
      {isMobile ? <MobileShell title="Aura">{body}</MobileShell> : body}
    </>
  );
};

export default AuraView;
